import { MutationCache, QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { ApiError } from "../api/client.js";

/**
 * Global mutation-error safety net (assessment U3 / gap 1.2).
 *
 * Previously a `mutate()` with no local error surface failed silently —
 * an archive/delete/save that hit a 4xx/5xx did nothing visible, and
 * only 4 of 44 route files wired a toast. This `MutationCache.onError`
 * turns every unhandled mutation failure into a toast in one place, and
 * (sonner announces toasts to assistive tech) closes the live-region
 * gap too.
 *
 * Forms that render their own inline field errors opt out per-mutation
 * with `meta: { suppressGlobalError: true }`, so we never double-surface.
 */
export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      if (mutation.meta?.["suppressGlobalError"]) return;
      const message =
        error instanceof ApiError ? error.message : "Something went wrong. Please try again.";
      toast.error(message);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});
